import { Handshake, Plus, Globe, UserCheck, ExternalLink } from 'lucide-preact'
import { useAuth } from '../../../auth/useAuth'
import { useEffect, useState } from 'preact/hooks'
import { clientCompanyApi } from '@/api'
import type { ClientCompanyViewDto } from '@/types/company'
import { AddCustomerModal } from './AddCustomerModal'
import { CompanyManagement } from '../company/CompanyManagement'
import { useLocation } from 'preact-iso'

export function SettingsCustomers({ clientCompanyId: routeCompanyId }: { clientCompanyId?: string }) {
    const { isAuthenticated, allWorkspaces } = useAuth()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [customers, setCustomers] = useState<ClientCompanyViewDto[] | null>(null)
    const [isAddModalOpen, setIsAddModalOpen] = useState(false)
    const [selectedCompany, setSelectedCompany] = useState<ClientCompanyViewDto | null>(null)
    const location = useLocation()

    // Get set of customer company IDs the user can actually access (from /me data)
    const accessibleCustomerIds = new Set(
        allWorkspaces
            ?.filter(ws => ws.ClientCompanyId !== routeCompanyId) // Exclude home company
            ?.map(ws => ws.ClientCompanyId)
    )

    // Check if user can manage a specific customer
    const canManageCustomer = (customerId: string) => {
        return accessibleCustomerIds.has(customerId)
    }

    const loadCustomers = async () => {
        if (!isAuthenticated || !routeCompanyId) return
        setLoading(true)
        setError(null)
        try {
            const res = await clientCompanyApi.getCustomersManagedByCompany(routeCompanyId)
            if (res.error) {
                setError(res.error)
                setCustomers([])
            } else {
                setCustomers(res.data || [])
            }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- backend error shape not typed yet
    } catch (e: any) {
            setError('Failed to load customers')
            setCustomers([])
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadCustomers()
    }, [isAuthenticated, routeCompanyId])

    const handleManageCompany = (company: ClientCompanyViewDto) => {
        // Let CompanyLayout bootstrap the company/workspaces and route appropriately
        location.route(`/company/${company.Id}`)
    }

    const handleCreated = async (_newCompany: ClientCompanyViewDto) => {
        // Optimistically update local list
        setCustomers(prev => {
            const list = prev ? [...prev] : []
            return [_newCompany, ...list]
        })
        // Route to company bootstrap; it will pick the first active workspace or fallback
        if (_newCompany?.Id) {
            location.route(`/company/${_newCompany.Id}`)
        }
    }

    return (
        <div className="px-6 py-4 max-w-7xl mx-auto">
            <div className="mb-6 flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-3">
                        <Handshake className="h-6 w-6 text-primary-600" />
                        Customers
                    </h1>
                    <p className="text-slate-600 dark:text-slate-300">Manage your customer companies.</p>
                </div>
                {!selectedCompany && (
                    <div>
                        <button
                            onClick={() => setIsAddModalOpen(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-primary-600 hover:bg-primary-700"
                        >
                            <Plus className="w-4 h-4" />
                            Add Customer
                        </button>
                    </div>
                )}
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 px-6 py-4">
                {loading && (
                    <div className="text-slate-600 dark:text-slate-300">Loading customers…</div>
                )}
                {error && (
                    <div className="text-red-600 dark:text-red-400">{error}</div>
                )}
                {!loading && !error && !selectedCompany && (
                    <div className="space-y-4">
                        {(!customers || customers.length === 0) && (
                            <div className="text-slate-600 dark:text-slate-300">No customers found.</div>
                        )}
                        {customers && customers.length > 0 && (
                            <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                                {customers.map((c) => (
                                    <li key={c.Id} className="py-3">
                                        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <div className="font-medium text-slate-900 dark:text-white truncate">{c.Name}</div>
                                                    <StatusBadge status={c.Status} />
                                                </div>
                                                <div className="mt-1 text-sm text-slate-600 dark:text-slate-300 flex flex-wrap gap-x-2 gap-y-1">
                                                    <span className="truncate">{c.Industry}</span>
                                                    <span>•</span>
                                                    <span className="truncate">{c.Size}</span>
                                                    {c.Workspaces?.length ? (
                                                        <>
                                                            <span>•</span>
                                                            <span className="truncate">{c.Workspaces.length} workspace{c.Workspaces.length === 1 ? '' : 's'}</span>
                                                        </>
                                                    ) : null}
                                                </div>
                                                <div className="mt-2 text-xs text-slate-500 dark:text-slate-400 flex flex-wrap items-center gap-3">
                                                    {c.CompanyUrl && (
                                                        <a href={c.CompanyUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300">
                                                            <Globe className="h-3.5 w-3.5" />
                                                            <span className="truncate max-w-[200px]">{safeHostname(c.CompanyUrl)}</span>
                                                            <ExternalLink className="h-3 w-3" />
                                                        </a>
                                                    )}
                                                    {c.LinkedInUrl && (
                                                        <a href={c.LinkedInUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300">
                                                            <UserCheck className="h-3.5 w-3.5" />
                                                            <span>LinkedIn</span>
                                                            <ExternalLink className="h-3 w-3" />
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                            {/* Right-side quick action */}
                                            <div className="mt-2 md:mt-0">
                                                {canManageCustomer(c.Id) ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleManageCompany(c)}
                                                        className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 text-sm hover:underline"
                                                    >
                                                        Manage Company
                                                    </button>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-slate-400 dark:text-slate-500 text-sm cursor-not-allowed">
                                                        No Access
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {!loading && !error && selectedCompany && (
                    <div className="space-y-4">
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => setSelectedCompany(null)}
                                    className="inline-flex items-center gap-2 text-slate-700 dark:text-slate-300 hover:underline"
                                >
                                    {/* simple unicode arrow to avoid extra imports */}
                                    ← Back to customers
                                </button>
                            </div>
                        </div>
                        <div>
                            {/* Reuse entire Company view in embedded mode, fetch by ID of clicked customer */}
                            <CompanyManagement mode="embedded" />
                        </div>
                    </div>
                )}
            </div>

            {/* Modal */}
            {isAddModalOpen && routeCompanyId && (
                <AddCustomerModal
                    isOpen={isAddModalOpen}
                    onClose={() => setIsAddModalOpen(false)}
                    managedByCompanyId={routeCompanyId}
                    onCreated={handleCreated}
                />
            )}
        </div>
    )
}

function safeHostname(url: string): string {
    try {
        const u = new URL(url)
        return u.hostname
    } catch {
        return url
    }
}

function StatusBadge({ status }: { status?: string }) {
    const s = (status || '').toLowerCase()
    const base = 'px-2 py-0.5 rounded-md text-[10px] font-medium border'
    if (s === 'active') {
        return <span className={`${base} bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300`}>Active</span>
    }
    if (s === 'inactive') {
        return <span className={`${base} bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300`}>Inactive</span>
    }
    return <span className={`${base} bg-slate-50 border-slate-200 text-slate-700 dark:bg-slate-700/30 dark:border-slate-600 dark:text-slate-300`}>{status || '—'}</span>
}
